import React, { useContext } from "react";
import TotalYearCard from "../../../components/totalrevenue/totalyearcard/TotalYearCard";
import TotalQuarterCard from "../../../components/totalrevenue/totalquarterard/TotalQuarterCard";
import TotalMonthCard from "../../../components/totalrevenue/totalmonthcard/TotalMonthCard";
import SubHeader from "../../../components/subheader/SubHeader";
import "./OnePlan.scss";
import RevenueTable from "../../../components/revenue/revenuetable/RevenueTable";
import RevenueTableCategories from "../../../components/revenue/revenuetablecategories/RevenueTableCategories";
import RevenueBarChart from "../../../components/revenue/revenuebarchart/RevenueBarChart";
import RegionalRevenueDonutChart from "../../../components/revenue/donutchart/DonutChart";
import ColumnsChart from "../../../components/revenue/onplan/columnschart/ColumnsChart";
import { getQuarterByMonth, createRevenueDateTitle, createRevenueCardYearTitle, createRevenueCardQuarterTitle, createRevenueCardMonthTitle } from "./../../../utils/format";

import {
  renameKeys,
  sumByRegion,
} from "../../../utils/formatdatarevenue/RevenueFormater";

import { AppContext } from "../../../context/Context";

const OnePlan = () => {
  const { data, fromDate, toDate } = useContext(AppContext);
  // console.log(data);

  // Current Revenue Card
  const allData = data?.revenue?.oneplan?.revenueoneplantototalincome || [];

  const firstRevenueYearMonth =
    allData.length > 0 ? allData[0].year_month : null;

  const lastRevenueYearMonth =
    allData.length > 0 ? allData[allData.length - 1].year_month : null;
  
  const computeRevenueCardYearAmount = (fromDate, toDate, data) => {
    let total = 0;

    if (fromDate === "All" || !toDate) {
      total =
        data.revenue?.oneplan.revenueoneplantototalincome?.reduce(
          (sum, item) => sum + (item.revenue_income || 0),
          0
        ) || 0;
    } else {
      const fromYear = parseInt(fromDate.split("-")[0]);
      const toYear = parseInt(toDate.split("-")[0]);

      total =
        data.revenue?.oneplan.revenueoneplantototalincome
          ?.filter((item) => {
            const itemYear = parseInt(item.year_month.split("-")[0]);
            return itemYear >= fromYear && itemYear <= toYear;
          })
          .reduce((sum, item) => sum + (item.revenue_income || 0), 0) || 0;
    }

    // Format with commas and 2 decimal places
    return total;
  };

  const computeRevenueCardQuarterAmount = (fromDate, toDate, data) => {
    if (!data?.revenue?.oneplan.revenueoneplantototalincome) return 0;

    // Handle "All"
    if (fromDate === "All" || !toDate) {
      return data.revenue.oneplan.revenueoneplantototalincome.reduce(
        (sum, item) => sum + (item.revenue_income || 0),
        0
      );
    }

    // --- Parse from/to year & month ---
    const [fromYear, fromMonth] = fromDate.split("-").map(Number);
    const [toYear, toMonth] = toDate.split("-").map(Number);

    const fromQuarter = getQuarterByMonth(fromMonth);
    const toQuarter = getQuarterByMonth(toMonth);

    // --- Filter ---
    const total = data.revenue.oneplan.revenueoneplantototalincome
      .filter((item) => {
        const [itemYear, itemMonth] = item.year_month.split("-").map(Number);
        const itemQuarter = getQuarterByMonth(itemMonth);

        // year before range
        if (itemYear < fromYear || itemYear > toYear) return false;

        // same year as start
        if (itemYear === fromYear && itemQuarter < fromQuarter) return false;

        // same year as end
        if (itemYear === toYear && itemQuarter > toQuarter) return false;

        // in between years → always included
        return true;
      })
      .reduce((sum, item) => sum + (item.revenue_income || 0), 0);

    return total;
  };

  const computeRevenueCardMonthAmount = (data) => {
    const lines = data?.revenue?.oneplan?.revenueoneplantotalcolumns;
    if (!Array.isArray(lines)) return "0.00";

    const total = lines.reduce(
      (sum, item) => sum + (item.revenue_income || 0),
      0
    );

    return total;
  };

  const computePreviousMonthAmount = (data) => {
    if (!data.revenue?.oneplan.revenueoneplanpreviousmonth) return 0;
    return data.revenue.oneplan.revenueoneplanpreviousmonth.reduce(
      (sum, item) => sum + item.revenue_income,
      0
    );
  }

  const formattedDataRemoveNone = Object.entries(
    sumByRegion(data.revenue?.oneplan.revenueoneplanregion)
  )
    .map(([region, totalRevenue]) => ({
      name: region,
      value: totalRevenue,
    }))
    .filter((item) => item.name !== "(None)")
    .sort((a, b) => b.value - a.value);

  const regionColors = {
    "Region 2": "#10b981",
    "Region 3": "#eab308",
    "Region 1": "#34d399",
    "(None)": "#9ca3af",
  };

  const formattedData = Object.entries(
    sumByRegion(data.revenue?.oneplan.revenueoneplanregion)
  )
    .map(([region, totalRevenue]) => ({
      region,
      totalRevenue,
      indicator: regionColors[region] || "#000000",
    }))
    .sort((a, b) => b.totalRevenue - a.totalRevenue);

  const sumByState = (data) => {
    if (!Array.isArray(data)) {
      console.error("Invalid data: Expected an array but got", data);
      return {};
    }
    return data.reduce((acc, item) => {
      acc[item.state] = (acc[item.state] || 0) + item.revenue_income;
      return acc;
    }, {});
  };

  const sumState = sumByState(data.revenue?.oneplan.revenueoneplanstates);

  const revenueStateArray = Object.entries(sumState)
    .map(([state, revenue]) => ({
      state,
      revenue: parseFloat(revenue.toFixed(2)),
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  // -------------------------------------- hisghest Market ----------------------------------------------

  const sumByMarket = (data) => {
    if (!Array.isArray(data)) {
      console.error("Invalid data: Expected an array but got", data);
      return {};
    }
    return data.reduce((acc, item) => {
      acc[item.market] = (acc[item.market] || 0) + item.revenue_income;
      return acc;
    }, {});
  };

  const sumMarket = sumByMarket(data.revenue?.oneplan.revenueoneplanmarket);

  const revenueMarketArray = Object.entries(sumMarket)
    .map(([state, revenue]) => ({
      state,
      revenue: parseFloat(revenue.toFixed(2)),
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  const transformedData = data.revenue?.oneplan.revenueoneplancategories
    .map((item) => {
      const lastPeriodValue = data.revenue.oneplan.revenueoneplancategories
      .filter(
        (x) =>
          x.account_dashboard_rename === item.account_dashboard_rename &&
          x.range_type === "previous"
      )
      .reduce((sum, x) => sum + (x.revenue_income || 0), 0);
      const currentValue = data.revenue.oneplan.revenueoneplancategories
      .filter(
        (x) =>
          x.account_dashboard_rename === item.account_dashboard_rename &&
          x.range_type === "current"
      )
      .reduce((sum, x) => sum + (x.revenue_income || 0), 0);
      
      // Safe percent change
      let percentChange = 0;
      if (lastPeriodValue === 0) {
        percentChange = currentValue > 0 ? 100 : 0;
      } else {
        percentChange = ((currentValue - lastPeriodValue) / lastPeriodValue) * 100;
      }

      return {
        account_dashboard_rename: item.account_dashboard_rename,
        revenue_income: currentValue,
        revenue_income_lastmonth: lastPeriodValue,
        percentChange,
      };
    })
    // prevent duplicates (optional)
    .filter(
      (v, i, a) =>
        a.findIndex(
          (t) => t.account_dashboard_rename === v.account_dashboard_rename
        ) === i
    )
    .sort((a, b) => b.revenue_income - a.revenue_income);

  return (
    <div className="frame-main-revenue-oneplan">
      <SubHeader title="REVENUE & INCOME (ONE PLAN)" />

      <div className="frame-block1-revenue-oneplan">
        <div className="frame-block1-revenue-oneplan-left">
          <div className="frame-block1-revenue-oneplan-left-container">
            <div className="frame-block1-revenue-oneplan-left-top">
              <div className="frame-block1-oneplan-left-top-left">
                <TotalYearCard
                  title={createRevenueCardYearTitle(firstRevenueYearMonth, lastRevenueYearMonth, fromDate, toDate)}
                  amount={computeRevenueCardYearAmount(fromDate, toDate, data)}
                />
              </div>
              <div className="frame-block2-oneplan-left-top-left">
                <TotalQuarterCard
                  title={createRevenueCardQuarterTitle(firstRevenueYearMonth, lastRevenueYearMonth, fromDate, toDate)}
                  amount={computeRevenueCardQuarterAmount(fromDate, toDate, data)}
                />
              </div>

              <div className="frame-block3-oneplan-left-top-left">
                <TotalMonthCard
                  title={createRevenueCardMonthTitle(firstRevenueYearMonth, lastRevenueYearMonth, fromDate, toDate)}
                  amount={computeRevenueCardMonthAmount(data)}
                  previousMonthAmount={computePreviousMonthAmount(data)}
                />
              </div>
            </div>
            <div className="frame-block1-revenue-oneplan-left-bottom">
              <ColumnsChart
                datas={renameKeys(
                  data.revenue?.oneplan.revenueoneplantototalincome
                )}
                firstColor="#E2FF8B"
                secondColor="#52B69A"
                height="100%"
              />
            </div>
          </div>
        </div>
        <div className="frame-block1-revenue-oneplan-right">
          <div className="frame-block1-revenue-oneplan-right-top">
            <h3>TOTAL REVENUE BY REGIONAL</h3>
            <div className="frame-donut-chart-main">
              <RegionalRevenueDonutChart
                colors={["#CCFF33", "#70E000", "#52B69A"]}
                data={formattedDataRemoveNone}
              />
            </div>
          </div>
          <div className="frame-block1-revenue-oneplan-right-bottom">
            <RevenueTable data={formattedData} />
          </div>
        </div>
      </div>
      <div className="frame-block2-revenue-oneplan">
        <div className="frame-block2-revenue-oneplan-left">
          <h3>TOTAL INCOME BY STATE</h3>
          <RevenueBarChart
            height={400}
            firstColor="#EAFFBD"
            secondColor="#52B69A"
            data={revenueStateArray}
          />
        </div>
        <div className="frame-block2-revenue-oneplan-right">
          <h3>TOTAL INCOME BY MARKET</h3>
          <RevenueBarChart
            height={400}
            firstColor="#EAFFBD"
            secondColor="#52B69A"
            data={revenueMarketArray}
          />
        </div>
      </div>

      <div className="frame-block4-revenue-oneplan">
        <h3>TOTAL REVENUE & INCOME BY CATEGORIES ({createRevenueDateTitle(firstRevenueYearMonth, lastRevenueYearMonth, fromDate, toDate)})</h3>
        <RevenueTableCategories
          data={transformedData}
           from_date={fromDate}
          headerTitle={[
            "Categories",
            "Total Revenue",
            "Total Revenue (Last Period)",
            "% Change",
          ]}
        />
      </div>
    </div>
  );
};

export default OnePlan;
